import { TogglableRecordsEditType } from "common/components/TogglableRecordsTable";
import { FeneTechManufacturerRecord } from "common/records/FeneTechManufacturer";
import { RootState } from "common/reducers";
import { tokenData, tokenImages } from "common/selectors/token.selectors";
import { IdType, Nullable } from "common/types";
import { List } from "immutable";
import { createSelector } from "reselect";

const kind = "fene_tech";

/**
 * Loads active manufacturers from the token data
 *
 * @param {RootState} state The RootState
 * @param {} options
 * @returns {List<FeneTechManufacturerRecord>} List<FeneTechManufacturerRecord>
 */
export const activeManufacturers = (state: RootState): List<FeneTechManufacturerRecord> =>
  tokenData(state, { kind })?.get("manufacturers") || List<FeneTechManufacturerRecord>();

/**
 * Loads available manufacturers from the token data
 *
 * @param {RootState} state The RootState
 * @param {} options
 * @returns {List<FeneTechManufacturerRecord>} List<FeneTechManufacturerRecord>
 */
export const availableManufacturers = (state: RootState): List<FeneTechManufacturerRecord> =>
  tokenData(state, { kind })?.get("availableManufacturers") || List<FeneTechManufacturerRecord>();

/**
 * Looks to active and available manufacturers to find the manufacturer with the given recordId
 *
 * @param {RootState} state The RootState
 * @param {IdType} recordId The manufacturer record id (a UUID string, not a number)
 * @returns {FeneTechManufacturerRecord | undefined} The matching manufacturer, or undefined when no manufacturer matches recordId
 */
export const manufacturer = createSelector(
  [activeManufacturers, availableManufacturers, (_, recordId: IdType) => recordId],
  (
    activeManufacturers: List<FeneTechManufacturerRecord>,
    availableManufacturers: List<FeneTechManufacturerRecord>,
    recordId: IdType,
  ) => {
    return activeManufacturers
      .concat(availableManufacturers)
      .find((m: FeneTechManufacturerRecord) => m.id === recordId);
  },
);

/**
 * Looks to active and available manufacturers to find the manufacturer with the given recordId and returns the index
 *
 * @param {RootState} state The RootState
 * @param recordId: number
 * @returns {number} number
 */
export const manufacturerIndex = (
  state: RootState,
  props: { recordId: Nullable<IdType>; editType: Nullable<TogglableRecordsEditType> },
) => {
  switch (props.editType) {
    case "available":
    case "add":
      return availableManufacturers(state).findIndex((m: FeneTechManufacturerRecord) => m.id === props.recordId);
    case "active":
      return activeManufacturers(state).findIndex((m: FeneTechManufacturerRecord) => m.id === props.recordId);
    default:
  }
};

/**
 * Loads the active manufacturer ids from the token data
 *
 * @param {RootState} state The RootState
 * @param recordId: number
 * @returns {List<string>} List<string>
 */
export const activeManufacturerIds = createSelector(
  [activeManufacturers],
  (manufacturers: List<FeneTechManufacturerRecord>): List<IdType> =>
    manufacturers.map((m: FeneTechManufacturerRecord) => m.id),
);

/**
 * Loads the available manufacturer ids from the token data
 *
 * @param {RootState} state The RootState
 * @param recordId: number
 * @returns {List<string>} List<string>
 */
export const availableManufacturerIds = createSelector(
  [availableManufacturers],
  (manufacturers: List<FeneTechManufacturerRecord>): List<IdType> =>
    manufacturers.map((m: FeneTechManufacturerRecord) => m.id),
);

/**
 * Looks to the token images to find the image of the manufacturer with the given image uuid
 *
 * @param {RootState} state The RootState
 * @param recordId: number
 * @returns {ImageRecord} ImageRecord
 */
export const manufacturerImage = (state: RootState, uuid: Nullable<string>) => {
  return tokenImages(state, { kind: "fene_tech" }).find((image) => image?.get("uuid") === uuid);
};
